import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { authAction, authSelector } from '../../features/auth/slice';
import { User, Lock, Mail, Phone, Camera, X } from 'lucide-react';
import PublicLayout from '../../components/public/PublicLayout';
import UserAvatar from '../../components/UserAvatar';

const EMAIL_DOMAINS = ['gmail.com', 'naver.com', 'daum.net', 'hanmail.net', 'nate.com', 'kakao.com', 'custom'];

function MemberEditProfilePage() {
  const { me } = useSelector(authSelector.selectAuthState);
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [profileImage, setProfileImage] = useState('');
  const [uploading, setUploading] = useState(false);
  const CATEGORIES = ['보컬','기타리스트','베이시스트','키보디스트','드러머','싱어송라이터','작곡가','작사가','프로듀서','기획사','뮤지션'];

  const [form, setForm] = useState({
    loginId: '',
    name: '',
    nickName: '',
    category: '뮤지션',
    emailId: '',
    emailDomain: 'gmail.com',
    customDomain: '',
    phone1: '',
    phone2: '',
    phone3: '',
    password: '',
    passwordConfirm: '',
  });

  const handleProfileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return alert('5MB 이하의 이미지만 업로드 가능합니다.');
    const formData = new FormData();
    formData.append('profileImage', file);
    setUploading(true);
    try {
      const res = await axios.post(`/member/${me.id}/profile-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProfileImage(res.data.profileImage);
      dispatch(authAction.changeMe({ ...me, profileImage: res.data.profileImage }));
    } catch {
      alert('프로필 사진 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleProfileDelete = async () => {
    if (!window.confirm('프로필 사진을 삭제하시겠습니까?')) return;
    try {
      await axios.delete(`/member/${me.id}/profile-image`);
      setProfileImage('');
      dispatch(authAction.changeMe({ ...me, profileImage: null }));
    } catch {
      alert('프로필 사진 삭제에 실패했습니다.');
    }
  };

  useEffect(() => {
    if (!me?.id) return;
    axios
      .get('/member/me')
      .then((res) => {
        const d = res.data;
        setProfileImage(d.profileImage || '');
        setForm({
          loginId: d.loginId || '',
          name: d.name || '',
          nickName: d.nickName || '',
          category: d.category || '뮤지션',
          emailId: d.emailId || '',
          emailDomain: d.emailDomain || 'gmail.com',
          customDomain: '',
          phone1: d.phone1 || '',
          phone2: d.phone2 || '',
          phone3: d.phone3 || '',
          password: '',
          passwordConfirm: '',
        });
      })
      .finally(() => setIsLoading(false));
  }, [me]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) return alert('이름은 필수 입력값입니다.');
    if (form.password && form.password !== form.passwordConfirm) {
      return alert('비밀번호가 일치하지 않습니다.');
    }
    if (form.password && form.password.length < 4) {
      return alert('비밀번호는 4자리 이상이어야 합니다.');
    }

    const emailDomain = form.emailDomain === 'custom' ? form.customDomain : form.emailDomain;

    const formData = new FormData();
    formData.append('name', form.name);
    formData.append('nickName', form.nickName);
    formData.append('category', form.category || '뮤지션');
    formData.append('emailId', form.emailId);
    formData.append('emailDomain', emailDomain);
    formData.append('phone1', form.phone1);
    formData.append('phone2', form.phone2);
    formData.append('phone3', form.phone3);
    if (form.password) formData.append('password', form.password);

    try {
      const res = await axios.put(`/member/${me.id}`, formData);
      dispatch(authAction.changeMe({ ...me, name: res.data.name, nickName: res.data.nickName }));
      alert('회원 정보가 수정되었습니다.');
    } catch (err) {
      alert(err.response?.data?.errorMessage || '수정에 실패했습니다.');
    }
  };

  const inputClass =
    'w-full pl-9 pr-4 py-2.5 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent transition';
  const inputClassNoPad =
    'w-full px-3 py-2.5 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent transition';
  const readonlyClass =
    'w-full pl-9 pr-4 py-2.5 rounded-xl border border-white/[0.06] bg-[#0d1526] text-sm text-slate-400 outline-none cursor-not-allowed';

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="flex justify-center items-center py-16 min-h-[60vh]">
          <div className="text-slate-400">로딩 중...</div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
    <div className="flex justify-center items-center py-16 px-4 min-h-[60vh]">
      <div className="w-full max-w-md">
        <div className="bg-[#0d1526] rounded-t-2xl px-8 pt-8 pb-6 border border-white/[0.08] border-b-0">
          <div className="flex items-center gap-4 mb-4">
            <div className="relative group">
              <UserAvatar
                name={form.name || me?.name}
                profileImage={profileImage}
                userType="member"
                adminYn="N"
                size={64}
                showRole={true}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity cursor-pointer">
                <Camera size={20} className="text-white" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleProfileUpload}
              />
              {profileImage && (
                <button
                  type="button"
                  onClick={handleProfileDelete}
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-rose-500 flex items-center justify-center text-white hover:bg-rose-600 transition-colors">
                  <X size={12} />
                </button>
              )}
            </div>
            <div>
              <h1 className="text-white text-2xl font-bold tracking-tight">내 정보 수정</h1>
              <p className="text-slate-400 text-sm mt-1">
                {uploading ? '업로드 중...' : '프로필 사진을 클릭하여 변경'}
              </p>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-[#182334] rounded-b-2xl px-8 py-8 space-y-5 border border-white/[0.08] border-t-0">
          {/* 아이디 (읽기전용) */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">아이디</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <User size={15} className="text-slate-500" />
              </div>
              <input type="text" className={readonlyClass} value={form.loginId} readOnly />
            </div>
          </div>

          {/* 이름 */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              이름 <span className="text-rose-400">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <User size={15} className="text-slate-500" />
              </div>
              <input type="text" className={inputClass} value={form.name} onChange={handleChange('name')} />
            </div>
          </div>

          {/* 닉네임 */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">닉네임</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <User size={15} className="text-slate-500" />
              </div>
              <input type="text" className={inputClass} placeholder="닉네임" value={form.nickName} onChange={handleChange('nickName')} />
            </div>
          </div>

          {/* 분류 */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">분류</label>
            <select
              className={inputClassNoPad}
              value={form.category}
              onChange={handleChange('category')}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* 비밀번호 */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              새 비밀번호 <span className="text-slate-500 normal-case">(변경 시에만 입력)</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Lock size={15} className="text-slate-500" />
              </div>
              <input
                type="password"
                className={inputClass}
                placeholder="새 비밀번호"
                value={form.password}
                onChange={handleChange('password')}
                autoComplete="new-password"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              새 비밀번호 확인
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Lock size={15} className="text-slate-500" />
              </div>
              <input
                type="password"
                className={inputClass}
                placeholder="비밀번호 재입력"
                value={form.passwordConfirm}
                onChange={handleChange('passwordConfirm')}
                autoComplete="new-password"
              />
            </div>
          </div>

          {/* 이메일 */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">이메일</label>
            <div className="flex items-center gap-1.5">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <Mail size={15} className="text-slate-500" />
                </div>
                <input type="text" className={inputClass} value={form.emailId} onChange={handleChange('emailId')} />
              </div>
              <span className="text-slate-500 text-sm">@</span>
              {form.emailDomain === 'custom' ? (
                <input
                  type="text"
                  className={`${inputClassNoPad} flex-1`}
                  placeholder="직접입력"
                  value={form.customDomain}
                  onChange={handleChange('customDomain')}
                />
              ) : (
                <select
                  className={`${inputClassNoPad} flex-1`}
                  value={form.emailDomain}
                  onChange={handleChange('emailDomain')}>
                  {EMAIL_DOMAINS.map((d) => (
                    <option key={d} value={d}>
                      {d === 'custom' ? '직접입력' : d}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {form.emailDomain === 'custom' && (
              <button
                type="button"
                onClick={() => setForm((p) => ({ ...p, emailDomain: 'gmail.com', customDomain: '' }))}
                className="text-xs text-sky-400 hover:underline mt-1">
                목록에서 선택
              </button>
            )}
          </div>

          {/* 전화번호 */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">전화번호</label>
            <div className="flex items-center gap-1.5">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <Phone size={15} className="text-slate-500" />
                </div>
                <input type="text" className={inputClass} value={form.phone1} onChange={handleChange('phone1')} maxLength={3} />
              </div>
              <span className="text-slate-500">-</span>
              <input type="text" className={inputClassNoPad + ' flex-1'} value={form.phone2} onChange={handleChange('phone2')} maxLength={4} />
              <span className="text-slate-500">-</span>
              <input type="text" className={inputClassNoPad + ' flex-1'} value={form.phone3} onChange={handleChange('phone3')} maxLength={4} />
            </div>
          </div>

          {/* 저장 */}
          <button
            type="submit"
            className="w-full flex items-center justify-center gap-2 px-4 py-3 mt-2 bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold rounded-xl transition-colors duration-200 cursor-pointer">
            저장
          </button>
        </form>
      </div>
    </div>
    </PublicLayout>
  );
}

export default MemberEditProfilePage;
